import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { indexedDBService } from '../services/indexedDBService';

// Request deduplication map
const pendingRequests = new Map<string, Promise<any>>();

// Create request key for deduplication
function getRequestKey(config: AxiosRequestConfig): string {
  return `${config.method}:${config.url}:${JSON.stringify(config.data || {})}`;
}

// Retry config
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000; // Start with 1 second
const CLOUDFLARE_ERROR_CODES = [520, 521, 522, 523, 524, 525, 526, 527, 530];

// Network health tracking
let consecutiveFailures = 0;
const MAX_FAILURES_BEFORE_WARNING = 3;

// Create axios instance with enhanced config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout (increased from 10s)
  // Only accept 2xx status codes as success
  validateStatus: (status) => status >= 200 && status < 300,
});

// Request interceptor to add JWT token and deduplication
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Skip auth for public catalog endpoints
    const publicEndpoints = ['/catalog/', '/auth/login'];
    const isPublicEndpoint = publicEndpoints.some(endpoint => 
      config.url?.includes(endpoint)
    );
    
    // Only add token if not a public endpoint
    if (!isPublicEndpoint) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Add retry count to config
    if (!(config as any).retryCount) {
      (config as any).retryCount = 0;
    }
    
    // Check for duplicate requests (except for POST mutations)
    const requestKey = getRequestKey(config);
    if (config.method?.toUpperCase() === 'GET' && pendingRequests.has(requestKey)) {
      // Return existing promise for GET requests
      return pendingRequests.get(requestKey)!.then(() => config);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with retry logic and offline handling
api.interceptors.response.use(
  (response) => {
    // Clean up pending request tracking
    const requestKey = getRequestKey(response.config);
    pendingRequests.delete(requestKey);
    
    // Reset failure counter on successful response
    consecutiveFailures = 0;
    
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as any;
    
    if (!config) {
      return Promise.reject(error);
    }
    
    // Clean up pending request tracking
    const requestKey = getRequestKey(config);
    pendingRequests.delete(requestKey);
    
    // Track consecutive failures
    consecutiveFailures++;
    
    // Detect Cloudflare errors
    const isCloudflareError = error.response && CLOUDFLARE_ERROR_CODES.includes(error.response.status);
    
    // Detect DNS resolution failures
    const isDNSError = error.code === 'ENOTFOUND' || 
                       error.code === 'EAI_AGAIN' ||
                       error.message?.includes('getaddrinfo') ||
                       error.message?.includes('DNS');
    
    // Detect connection refused (server down)
    const isConnectionRefused = error.code === 'ECONNREFUSED' ||
                                error.message?.includes('ECONNREFUSED');
    
    // Detect SSL/TLS errors
    const isSSLError = error.code === 'CERT_HAS_EXPIRED' ||
                       error.code === 'UNABLE_TO_VERIFY_LEAF_SIGNATURE' ||
                       error.message?.includes('certificate') ||
                       error.message?.includes('SSL');
    
    // Retry logic for network errors, Cloudflare errors, or 5xx errors
    const shouldRetry = (
      !error.response || // Network error
      (error.response.status >= 500 && error.response.status < 600) || // Server error
      isCloudflareError || // Cloudflare proxy error
      error.code === 'ECONNABORTED' || // Timeout
      error.code === 'ERR_NETWORK' || // Network error
      isDNSError || // DNS resolution failed
      isConnectionRefused // Connection refused
    );
    
    if (shouldRetry && config.retryCount < MAX_RETRIES) {
      config.retryCount++;
      
      // Exponential backoff: 1s, 2s, 4s
      const delayMs = RETRY_DELAY_MS * Math.pow(2, config.retryCount - 1);
      
      console.log('Retrying request (attempt ' + config.retryCount + '/' + MAX_RETRIES + ') after ' + delayMs + 'ms...');
      
      if (isCloudflareError) {
        console.warn('Cloudflare error ' + error.response?.status + ' detected. Retrying...');
      }
      if (error.response?.status === 404) {
        console.warn('Resource not found (404):', config.url);
        
        // Check if it's a routing issue vs API endpoint issue
        if (config.url?.includes('/api/')) {
          console.warn('API endpoint not found. Check if backend is updated.');
        }
      }
      
      // Handle Cloudflare errors (520-530)
      if (isCloudflareError) {
        const cloudflareErrors: Record<number, string> = {
          520: 'Web server returned unknown error',
          521: 'Web server is down',
          522: 'Connection timed out',
          523: 'Origin is unreachable',
          524: 'A timeout occurred',
          525: 'SSL handshake failed',
          526: 'Invalid SSL certificate',
          527: 'Railgun error',
          530: 'Cloudflare DNS error'
        };
        console.error('Cloudflare error ' + error.response.status + ':', cloudflareErrors[error.response.status] || 'Unknown Cloudflare error');
      }
      
      // Handle 500 Internal Server Error
      if (error.response.status >= 500 && error.response.status < 520) {
        console.error('Server error:', error.response.data);
      }
      
      // Ensure token is still attached for retry
      const token = localStorage.getItem('token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else if (isDNSError) {
      // DNS resolution failed
      console.error('DNS Resolution Error:', error.message);
      console.warn('Cannot resolve domain name. Check DNS settings or internet connection.');
      
      window.dispatchEvent(new CustomEvent('dns-error', {
        detail: { message: 'Domain tidak dapat diakses. Periksa koneksi internet atau pengaturan DNS.' }
      }));
    } else if (isSSLError) {
      // SSL/TLS certificate error
      console.error('SSL/TLS Error:', error.message);
      console.warn('Certificate validation failed. Check SSL configuration.');
      
      window.dispatchEvent(new CustomEvent('ssl-error', {
        detail: { message: 'Sertifikat SSL tidak valid. Periksa konfigurasi keamanan.' }
      }));
    } else if (isConnectionRefused) {
      // Connection refused - server is not accepting connections
      console.error('Connection Refused:', error.message);
      console.warn('Server is not responding. It may be down or firewall is blocking.');
      
      window.dispatchEvent(new CustomEvent('server-down', {
        detail: { message: 'Server tidak merespons. Server mungkin sedang down atau maintenance.' }
      }));
      return api.request(config);
    }
    
    // Show warning after multiple consecutive failures
    if (consecutiveFailures >= MAX_FAILURES_BEFORE_WARNING) {
      console.error(consecutiveFailures + ' consecutive request failures. Network or server may be down.');
      
      // Dispatch custom event for UI notification
      window.dispatchEvent(new CustomEvent('network-degraded', {
        detail: {
          consecutiveFailures,
          lastError: error.message,
          isCloudflareError,
          isDNSError,
          isSSLError
        }
      }));
    }
    
    if (error.response) {
      // Handle 401 Unauthorized - token expired or invalid
      if (error.response.status === 401) {
        console.warn('⚠️ 401 Unauthorized detected:', config.url);
        
        // Skip redirect for public catalog endpoints
        const publicEndpoints = ['/catalog/'];
        const isPublicEndpoint = publicEndpoints.some(endpoint => 
          config.url?.includes(endpoint)
        );
        
        if (!isPublicEndpoint) {
          console.error('🔒 Authentication failed. Token may be expired or invalid.');
          console.log('Current token exists:', !!localStorage.getItem('token'));
          
          // Get current path to avoid logging out during critical operations
          const currentPath = window.location.pathname;
          const isCriticalPath = currentPath === '/pos' || currentPath === '/cashier';
          
          // Only logout if we're not already on login/catalog page and not in critical operation
          if (currentPath !== '/login' && currentPath !== '/catalog') {
            // If we're on a critical path (POS/Cashier), delay logout and show warning
            if (isCriticalPath) {
              console.warn('⚠️ Auth error on critical path. User should save work and re-login.');
              // Dispatch custom event for UI notification
              window.dispatchEvent(new CustomEvent('auth-warning', {
                detail: { 
                  message: 'Sesi akan berakhir. Selesaikan transaksi dan login ulang.',
                  path: currentPath,
                  url: config.url
                }
              }));
              // Don't auto-logout on POS screen - let user finish transaction
              return Promise.reject(error);
            } else {
              // Safe to logout on non-critical paths
              console.log('🚪 Logging out user...');
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              window.location.href = '/login';
            }
          }
        }
      }
      
      // Handle 403 Forbidden - insufficient permissions
      if (error.response.status === 403) {
        console.error('Access denied:', error.response.data);
      }
      
      // Handle 404 Not Found
      if (error.response.status === 404) {
        console.error('Resource not found:', error.response.data);
      }
      
      // Handle 500 Internal Server Error
      if (error.response.status >= 500) {
        console.error('Server error:', error.response.data);
      }
    } else if (!navigator.onLine || error.code === 'ERR_NETWORK') {
      // Network error - definitely offline
      console.error('Network error - offline mode:', error.message);
      
      // Check if this is a mutation that should be queued
      if (config.url && shouldQueueRequest(config.url)) {
        console.log('📦 Queueing request for later sync...');
        
        try {
          await queueRequestForSync(config);
          console.log('✓ Request queued successfully');
          
          // Dispatch offline event
          window.dispatchEvent(new CustomEvent('request-queued', { 
            detail: { endpoint: config.url } 
          }));
        } catch (queueError) {
          console.error('Failed to queue request:', queueError);
        }
      }
    } else {
      // Other errors (timeout, etc)
      console.error('Request error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Helper functions for offline queue
function shouldQueueRequest(url: string): boolean {
  const queueableEndpoints = ['/sales/', '/mutations/', '/returns/'];
  return queueableEndpoints.some(endpoint => url.includes(endpoint));
}

async function queueRequestForSync(config: any): Promise<void> {
  try {
    const endpoint = config.url || '';
    const data = config.data ? JSON.parse(config.data) : {};
    
    let type: 'sale' | 'mutation' | 'return' = 'sale';
    if (endpoint.includes('/mutations/')) type = 'mutation';
    if (endpoint.includes('/returns/')) type = 'return';

    await indexedDBService.addToSyncQueue({
      type,
      endpoint,
      data
    });
  } catch (error) {
    console.error('Error queueing request:', error);
    throw error;
  }
}

export default api;
